import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './Profile.css';

import API_URL from '../../config';
import { PostFileGrid } from '../Feed/Feed';
import Topbar from '../../components/Topbar/Topbar';
import { getUserGradient } from '../../utils/userGradient';


const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [theme] = useState(() => localStorage.getItem('projabze_theme') || 'dark');
  const [currentUser] = useState(() => {
    const stored = localStorage.getItem('projabze_user');
    return stored ? JSON.parse(stored) : null;
  });
  const token = localStorage.getItem('projabze_token');

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('posts');
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);

  const isOwnProfile = currentUser && (currentUser.id === userId || currentUser._id === userId);

  const SECTOR_ROLE_LABELS = {
    engineer_freelance: 'Serbest Muellif',
    engineer_bureau: 'Buro Calisani',
    inspector: 'Yapi Denetim Muhendisi',
    authority: 'Belediye Denetcisi',
    student: 'Ogrenci',
    supplier: 'Tedarikci',
    employer: 'Isveren',
  };

  const BADGE_DISPLAY = {
    tesisat_ustasi: '\u{1F50C} Tesisat Ustasi',
    mevzuat_bekcisi: '\u{1F4DA} Mevzuat Bekcisi',
    proje_mimari: '\u{1F3C6} Proje Mimari',
    mentor: '\u{1F393} Mentor',
    sektor_sesi: '\u26A1 Sektor Sesi',
    sablon_ustasi: '\u{1F4D0} Sablon Ustasi',
  };

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(`${API_URL}/api/social/users/${userId}/profile`, { headers });
        if (res.ok) {
          const data = await res.json();
          // Backend yanıtı { user, profile, company, stats, isFollowing, cvBundle }
          // şeklinde sarılı geliyor; UI flat erişim beklediği için açarak kullanıyoruz.
          setProfile({
            ...(data.user || {}),
            ...(data.profile || {}),
            company: data.company,
            stats: data.stats,
            isFollowing: data.isFollowing,
            cvBundle: data.cvBundle,
          });
        }
      } catch (err) {
        console.error('Profil yuklenirken hata:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId, token]);

  // Fetch user posts
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(`${API_URL}/api/feed/user/${userId}`, { headers });
        if (res.ok) {
          const data = await res.json();
          setPosts(Array.isArray(data) ? data : data.posts || []);
        }
      } catch (err) {
        console.error('Gonderiler yuklenirken hata:', err);
      }
    };

    fetchPosts();
  }, [userId, token]);

  // Follow / Unfollow
  const handleFollowToggle = async () => {
    if (!token || !currentUser) return navigate('/');
    setFollowLoading(true);
    try {
      if (profile.isFollowing) {
        const res = await fetch(`${API_URL}/api/social/unfollow/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          setProfile(prev => ({
            ...prev,
            isFollowing: false,
            stats: { ...prev.stats, followers: Math.max(0, (prev.stats?.followers || 0) - 1) },
          }));
        }
      } else {
        const res = await fetch(`${API_URL}/api/social/follows`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ followingId: userId }),
        });
        if (res.ok) {
          setProfile(prev => ({
            ...prev,
            isFollowing: true,
            stats: { ...prev.stats, followers: (prev.stats?.followers || 0) + 1 },
          }));
        }
      }
    } catch (err) {
      console.error('Takip islemi hatasi:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  // Send Message
  const handleSendMessage = async () => {
    if (!token || !currentUser) return navigate('/');
    setMessageLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/messages/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ recipientId: userId }),
      });
      if (res.ok) {
        navigate('/messages');
      }
    } catch (err) {
      console.error('Mesaj baslatilamadi:', err);
    } finally {
      setMessageLoading(false);
    }
  };

  // Delete post
  const handleDeletePost = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/feed/posts/${postId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPosts(prev => prev.filter(p => p.id !== postId));
      }
    } catch (err) { /* ignore */ }
  };

  // Time ago helper
  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Az once';
    if (diff < 3600) return `${Math.floor(diff / 60)} dk once`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat once`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} gun once`;
    return date.toLocaleDateString('tr-TR');
  };

  const initials = profile
    ? (profile.firstName?.[0] || '') + (profile.lastName?.[0] || '')
    : '';

  if (loading) {
    return (
      <div className={`profile-page ${theme}`}>
        <div className="profile-loading">
          <div className="profile-loading-spinner"></div>
          <p>Profil yukleniyor...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`profile-page ${theme}`}>
        <div className="profile-loading">
          <p>Kullanici bulunamadi.</p>
          <button className="profile-btn-back" onClick={() => navigate(-1)}>Geri Don</button>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('projabze_token');
    localStorage.removeItem('projabze_user');
    navigate('/');
  };

  return (
    <div className={`profile-page ${theme}`}>
      {/* Diğer sayfalardaki ortak Topbar — bildirim/menü dropdown'ları ile birlikte */}
      <Topbar user={currentUser} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="profile-container">
        {/* Banner — Ağım kullanıcı kartlarındakiyle aynı SVG dalga geçişi.
            getUserGradient(userId) ile her kullanıcıya özgü 3-renk paleti. */}
        {(() => {
          const g = getUserGradient(profile.id || userId);
          const waveId = `pwg-${(profile.id || userId || 'anon').toString().slice(0, 8)}`;
          return (
            <div
              className="profile-banner"
              style={{ background: `linear-gradient(135deg, ${g[0]}30, ${g[1]}18, ${g[2]}10)` }}
            >
              <svg className="profile-banner-wave" viewBox="0 0 400 200" preserveAspectRatio="none">
                <defs>
                  <linearGradient id={`${waveId}-1`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={g[0]} stopOpacity="0.45" />
                    <stop offset="100%" stopColor={g[1]} stopOpacity="0.10" />
                  </linearGradient>
                  <linearGradient id={`${waveId}-2`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={g[1]} stopOpacity="0.10" />
                    <stop offset="100%" stopColor={g[2]} stopOpacity="0.40" />
                  </linearGradient>
                </defs>
                <path d="M0,0 C80,40 150,100 200,100 C250,100 320,40 400,200 L400,200 L0,200 Z" fill={`url(#${waveId}-1)`} />
                <path d="M0,0 L0,0 C80,60 160,120 220,110 C280,100 350,160 400,200 L400,0 Z" fill={`url(#${waveId}-2)`} />
              </svg>
              <div className="profile-avatar">
                {profile.avatar
                  ? <img src={API_URL + profile.avatar} alt={profile.firstName} />
                  : <span className="profile-avatar-initials">{initials}</span>
                }
              </div>
            </div>
          );
        })()}

        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-header-inner">
            {/* Info */}
            <div className="profile-info">
              <div className="profile-info-top">
                <div className="profile-name-area">
                  <h1 className="profile-fullname">
                    {profile.firstName} {profile.lastName}
                    {profile.sectorRole && SECTOR_ROLE_LABELS[profile.sectorRole] && (
                      <span className={`profile-sector-badge profile-sector-${profile.sectorRole}`}>
                        {SECTOR_ROLE_LABELS[profile.sectorRole]}
                      </span>
                    )}
                  </h1>
                  {profile.bio && <p className="profile-bio">{profile.bio}</p>}
                  {profile.city && (
                    <div className="profile-location">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <span>{profile.city}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="profile-actions">
                  {isOwnProfile ? (
                    <button className="profile-btn profile-btn-edit" onClick={() => navigate('/settings')}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Profili Duzenle
                    </button>
                  ) : (
                    <>
                      <button
                        className={`profile-btn ${profile.isFollowing ? 'profile-btn-following' : 'profile-btn-follow'}`}
                        onClick={handleFollowToggle}
                        disabled={followLoading}
                      >
                        {followLoading ? (
                          <span className="profile-btn-spinner"></span>
                        ) : profile.isFollowing ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Takip Ediliyor
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                              <circle cx="8.5" cy="7" r="4" />
                              <line x1="20" y1="8" x2="20" y2="14" />
                              <line x1="23" y1="11" x2="17" y2="11" />
                            </svg>
                            Takip Et
                          </>
                        )}
                      </button>
                      <button
                        className="profile-btn profile-btn-message"
                        onClick={handleSendMessage}
                        disabled={messageLoading}
                      >
                        {messageLoading ? (
                          <span className="profile-btn-spinner"></span>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                            Mesaj Gonder
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Stats Row — Takipçi/Takip Edilen Ağım sayfasında; burada
                  yalnızca gönderi sayısı tutuldu. */}
              <div className="profile-stats">
                <div className="profile-stat">
                  <span className="profile-stat-value">{profile.stats?.posts ?? posts.length}</span>
                  <span className="profile-stat-label">Gönderi</span>
                </div>
              </div>

              {/* XP & Level */}
              {(profile.xp != null || profile.level) && (
                <div className="profile-xp-row">
                  {profile.xp != null && (
                    <span className="profile-xp-value">{profile.xp} XP</span>
                  )}
                  {profile.level && (
                    <span className={`profile-level-badge profile-level-${profile.level}`}>
                      {profile.level.charAt(0).toUpperCase() + profile.level.slice(1)}
                    </span>
                  )}
                </div>
              )}

              {/* Badges */}
              {profile.badges && profile.badges.length > 0 && (
                <div className="profile-badges-row">
                  {profile.badges.map((badgeId) => (
                    BADGE_DISPLAY[badgeId] && (
                      <span key={badgeId} className="profile-badge-item">
                        {BADGE_DISPLAY[badgeId]}
                      </span>
                    )
                  ))}
                </div>
              )}

              {/* Social Links */}
              {(profile.linkedinUrl || profile.twitterUrl || profile.websiteUrl) && (
                <div className="profile-social-row">
                  {profile.linkedinUrl && (
                    <a href={profile.linkedinUrl} target="_blank" rel="noopener noreferrer" className="profile-social-link" title="LinkedIn">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      <span>LinkedIn</span>
                    </a>
                  )}
                  {profile.twitterUrl && (
                    <a href={profile.twitterUrl} target="_blank" rel="noopener noreferrer" className="profile-social-link" title="Twitter">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <span>Twitter</span>
                    </a>
                  )}
                  {profile.websiteUrl && (
                    <a href={profile.websiteUrl} target="_blank" rel="noopener noreferrer" className="profile-social-link" title="Website">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="2" y1="12" x2="22" y2="12" />
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                      </svg>
                      <span>Website</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="profile-tabs">
          <button
            className={`profile-tab ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="9" y1="21" x2="9" y2="9" />
            </svg>
            Gonderiler
          </button>
          <button
            className={`profile-tab ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            Hakkinda
          </button>
        </div>

        {/* Tab Content */}
        <div className="profile-content">
          {activeTab === 'posts' && (
            <div className="profile-posts">
              {posts.length === 0 ? (
                <div className="profile-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                    <line x1="9" y1="21" x2="9" y2="9" />
                  </svg>
                  <h3>Henuz gonderi yok</h3>
                  <p>Bu kullanici henuz bir gonderi paylasmamis.</p>
                </div>
              ) : (
                posts.map((post) => (
                  <div className="profile-post-card" key={post._id || post.id}>
                    {/* Post Header */}
                    <div className="profile-post-header">
                      <div className="profile-post-avatar" onClick={() => {}}>
                        {profile.avatar
                          ? <img src={API_URL + profile.avatar} alt="" />
                          : <span>{initials}</span>
                        }
                      </div>
                      <div className="profile-post-meta">
                        <span className="profile-post-author">
                          {profile.firstName} {profile.lastName}
                          {profile.sectorRole && <span className="profile-post-role-badge">{({engineer_freelance:'Muellif',engineer_bureau:'Buro',inspector:'YD',authority:'Belediye',student:'Ogrenci',supplier:'Tedarikci',employer:'Isveren'})[profile.sectorRole] || ''}</span>}
                        </span>
                        <span className="profile-post-time">
                          {timeAgo(post.createdAt)}
                          {post.discipline && <span className="profile-post-disc-badge">{({electrical:'Kuvvetli Akim',fire:'Yangin',weak:'Zayif Akim'})[post.discipline]}</span>}
                        </span>
                      </div>
                      {post.title && <h3 className="profile-post-title">{post.title}</h3>}
                      <span className={`profile-post-type-chip type-${post.type || 'post'}`}>
                        {({post:'Paylasim',question:'Soru',regulation:'Mevzuat',project_showcase:'Proje',field_warning:'Uyari',product_review:'Inceleme',poll:'Anket',media:'Medya',document:'Dokuman',link:'Link'})[post.type || 'post']}
                      </span>
                      {isOwnProfile && (
                        <>
                          <button className={`profile-post-edit ${editingPostId === (post._id || post.id) ? 'active' : ''}`} title={editingPostId === (post._id || post.id) ? 'Duzenlemeyi kapat' : 'Duzenle'} onClick={() => setEditingPostId(editingPostId === (post._id || post.id) ? null : (post._id || post.id))}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button className="profile-post-delete" onClick={() => handleDeletePost(post._id || post.id)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        </>
                      )}
                    </div>

                    {/* Post Content */}
                    {post.content && (
                      <p className="profile-post-content">{post.content}</p>
                    )}

                    {/* Tags */}
                    {post.tags?.length > 0 && (
                      <div className="profile-post-tags">{post.tags.map((t, i) => <span key={i} className="profile-post-tag">#{t}</span>)}</div>
                    )}

                    {/* File Grid */}
                    <PostFileGrid
                      mediaUrls={post.mediaUrls}
                      apiUrl={API_URL}
                      downloadableIndexes={post.downloadableIndexes || []}
                      defaultPreviewIndex={post.defaultPreviewIndex ?? null}
                      postId={post._id || post.id}
                      isOwner={isOwnProfile}
                      token={token}
                      editMode={editingPostId === (post._id || post.id)}
                    />

                    {/* Post Footer */}
                    <div className="profile-post-footer">
                      <div className="profile-post-action">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        <span>{post.likeCount ?? post.likes?.length ?? 0}</span>
                      </div>
                      <div className="profile-post-action">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        <span>{post.commentCount ?? post.comments?.length ?? 0}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'about' && (
            <div className="profile-about">
              {/* Bio */}
              {profile.bio && (
                <div className="profile-about-card profile-about-bio">
                  <div className="profile-about-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </div>
                  <div className="profile-about-info">
                    <h4>Hakkımda</h4>
                    <p style={{ whiteSpace: 'pre-line' }}>{profile.bio}</p>
                  </div>
                </div>
              )}
              {/* CV — sunucudan gelen cvBundle (Settings/Özgeçmiş'te kaydedilmiş) */}
              {profile.cvBundle?.experienceList?.length > 0 && (
                <section className="profile-cv-section">
                  <h3 className="profile-cv-heading">Deneyim</h3>
                  <ul className="profile-cv-list">
                    {profile.cvBundle.experienceList.map(exp => (
                      <li key={exp.id} className="profile-cv-item">
                        <div className="profile-cv-row">
                          <strong>{exp.position || '—'}{exp.company ? ` · ${exp.company}` : ''}</strong>
                          <span className="profile-cv-year">
                            {exp.startDate || '—'} → {exp.current ? 'Devam' : (exp.endDate || '—')}
                          </span>
                        </div>
                        {exp.location && <div className="profile-cv-sub">{exp.location}</div>}
                        {exp.description && <p className="profile-cv-desc">{exp.description}</p>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {profile.cvBundle?.projectTypeList?.length > 0 && (
                <section className="profile-cv-section">
                  <h3 className="profile-cv-heading">Proje Tecrübeleri</h3>
                  <ul className="profile-cv-list">
                    {profile.cvBundle.projectTypeList.map(pt => (
                      <li key={pt.id} className="profile-cv-item">
                        <div className="profile-cv-row">
                          <strong>{pt.name}</strong>
                          {(pt.count || pt.years) && (
                            <span className="profile-cv-year">
                              {pt.count ? `${pt.count} proje` : ''}{pt.count && pt.years ? ' · ' : ''}{pt.years ? `${pt.years} yıl` : ''}
                            </span>
                          )}
                        </div>
                        {pt.category && <div className="profile-cv-sub">{pt.category}</div>}
                        {pt.notes && <p className="profile-cv-desc">{pt.notes}</p>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {profile.cvBundle?.skillList?.length > 0 && (
                <section className="profile-cv-section">
                  <h3 className="profile-cv-heading">Yazılım & Teknik Yetkinlikler</h3>
                  <div className="profile-cv-skills">
                    {profile.cvBundle.skillList.map(s => (
                      <span key={s.id} className={`profile-cv-skill skill-lv-${s.level || 'intermediate'}`}>
                        <span>{s.name}</span>
                        {s.years && <small>{s.years} yıl</small>}
                      </span>
                    ))}
                  </div>
                </section>
              )}
              {profile.cvBundle?.educationList?.length > 0 && (
                <section className="profile-cv-section">
                  <h3 className="profile-cv-heading">Eğitim</h3>
                  <ul className="profile-cv-list">
                    {profile.cvBundle.educationList.map((edu, idx) => (
                      <li key={idx} className="profile-cv-item">
                        <div className="profile-cv-row">
                          <strong>{edu.school || '—'}</strong>
                          {edu.graduationYear && <span className="profile-cv-year">{edu.graduationYear}</span>}
                        </div>
                        <div className="profile-cv-sub">
                          {edu.faculty && <span>{edu.faculty}</span>}
                          {edu.department && <span>{edu.department && edu.faculty ? ' · ' : ''}{edu.department}</span>}
                          {edu.gpa && <span> · GNO: {edu.gpa}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {profile.cvBundle?.certificateList?.length > 0 && (
                <section className="profile-cv-section">
                  <h3 className="profile-cv-heading">Sertifikalar & Yetki Belgeleri</h3>
                  <ul className="profile-cv-list">
                    {profile.cvBundle.certificateList.filter(c => (c.visibility || 'public') !== 'private').map(cert => (
                      <li key={cert.id} className="profile-cv-item">
                        <div className="profile-cv-row">
                          <strong>{cert.name}</strong>
                          {(cert.date || cert.expiry) && (
                            <span className="profile-cv-year">
                              {cert.date || ''}{cert.date && cert.expiry ? ' — ' : ''}{cert.expiry || ''}
                            </span>
                          )}
                        </div>
                        <div className="profile-cv-sub">
                          {cert.issuer && <span>{cert.issuer}</span>}
                          {cert.scope && <span> · {cert.scope}</span>}
                        </div>
                        {cert.notes && <p className="profile-cv-desc">{cert.notes}</p>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {profile.cvBundle?.licenseList?.length > 0 && (
                <section className="profile-cv-section">
                  <h3 className="profile-cv-heading">Ehliyet & Diğer Belgeler</h3>
                  <ul className="profile-cv-list">
                    {profile.cvBundle.licenseList.map(lic => (
                      <li key={lic.id} className="profile-cv-item">
                        <div className="profile-cv-row">
                          <strong>{lic.name}</strong>
                          {(lic.date || lic.expiry) && (
                            <span className="profile-cv-year">
                              {lic.date || ''}{lic.date && lic.expiry ? ' — ' : ''}{lic.expiry || ''}
                            </span>
                          )}
                        </div>
                        <div className="profile-cv-sub">
                          {lic.issuer && <span>{lic.issuer}</span>}
                          {lic.scope && <span> · {lic.scope}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Company */}
              {profile.company && (
                <div className="profile-about-card">
                  <div className="profile-about-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                  </div>
                  <div className="profile-about-info">
                    <h4>Sirket</h4>
                    <p>{typeof profile.company === 'object' ? profile.company.name : profile.company}</p>
                  </div>
                </div>
              )}

              {/* Title / Role */}
              {profile.title && (
                <div className="profile-about-card">
                  <div className="profile-about-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <div className="profile-about-info">
                    <h4>Unvan</h4>
                    <p>{profile.title}</p>
                  </div>
                </div>
              )}

              {/* Education */}
              {profile.education && profile.education.length > 0 && (
                <div className="profile-about-card">
                  <div className="profile-about-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                      <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
                    </svg>
                  </div>
                  <div className="profile-about-info">
                    <h4>Egitim</h4>
                    {profile.education.map((edu, i) => (
                      <p key={i}>
                        {edu.school || edu.institution}
                        {edu.degree && ` - ${edu.degree}`}
                        {edu.year && ` (${edu.year})`}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* City */}
              {profile.city && (
                <div className="profile-about-card">
                  <div className="profile-about-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div className="profile-about-info">
                    <h4>Konum</h4>
                    <p>{profile.city}</p>
                  </div>
                </div>
              )}

              {/* Website */}
              {profile.website && (
                <div className="profile-about-card">
                  <div className="profile-about-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </div>
                  <div className="profile-about-info">
                    <h4>Website</h4>
                    <a href={profile.website} target="_blank" rel="noopener noreferrer">{profile.website}</a>
                  </div>
                </div>
              )}

              {/* Certificates */}
              {profile.certificates && (
                <div className="profile-about-card">
                  <div className="profile-about-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="7" />
                      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                    </svg>
                  </div>
                  <div className="profile-about-info">
                    <h4>Sertifikalar</h4>
                    <p>{profile.certificates}</p>
                  </div>
                </div>
              )}

              {/* Member since */}
              {profile.createdAt && (
                <div className="profile-about-card">
                  <div className="profile-about-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </div>
                  <div className="profile-about-info">
                    <h4>Uyelik Tarihi</h4>
                    <p>{new Date(profile.createdAt).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              )}

              {/* Empty about state */}
              {!profile.bio && !profile.company && !profile.title && !profile.city && !profile.website && (!profile.education || profile.education.length === 0) && !profile.certificates && !profile.cvBundle?.experienceList?.length && !profile.cvBundle?.educationList?.length && !profile.cvBundle?.skillList?.length && !profile.cvBundle?.certificateList?.length && !profile.cvBundle?.licenseList?.length && !profile.cvBundle?.projectTypeList?.length && (
                <div className="profile-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <h3>Profil bilgisi yok</h3>
                  <p>Bu kullanici henuz profil bilgilerini tamamlamamis.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
